import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, AlertCircle, CheckCircle, XCircle } from "lucide-react";

const CancellationPolicy = ({ user, onLogout }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl bg-black/80 border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <img 
                src="/elite-cwb-logo.webp" 
                alt="Elite CWB Logo" 
                className="h-24 w-auto" style={{ filter: 'drop-shadow(0 0 10px rgba(212, 175, 55, 0.3))' }}
              />
            </div>
            <Button 
              data-testid="back-to-home-policy-btn"
              variant="outline" 
              onClick={() => navigate("/")}
              className="border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <div className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Cancellation Policy</h1>
            <p className="text-lg text-gray-400">Understanding our booking and cancellation terms</p>
          </div>

          <Card className="bg-gradient-to-br from-gray-900 to-black border-amber-500/20 shadow-2xl mb-8">
            <CardContent className="p-8">
              <div className="space-y-8">
                {/* 48 Hour Policy */}
                <div>
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-2">Cancellations 48+ Hours Before Service</h3>
                      <p className="text-gray-300 text-lg">
                        Full deposit refund available. Cancel your booking through your dashboard or contact us directly.
                      </p>
                    </div>
                  </div>
                  <div className="ml-16 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-green-400">
                      ✓ 100% deposit refund<br />
                      ✓ Processed within 5-7 business days<br />
                      ✓ No cancellation fees
                    </p>
                  </div>
                </div>

                {/* Within 48 Hours */}
                <div>
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                      <XCircle className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-2">Cancellations Within 48 Hours</h3>
                      <p className="text-gray-300 text-lg">
                        No deposit refund available due to last-minute scheduling constraints.
                      </p>
                    </div>
                  </div>
                  <div className="ml-16 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-400">
                      ✗ No deposit refund<br />
                      ✗ Full deposit (20%) will be retained<br />
                      ✗ Balance payment not required
                    </p>
                  </div>
                </div>

                {/* How to Cancel */}
                <div className="border-t border-gray-800 pt-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-6 h-6 text-amber-500" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-4">How to Cancel Your Booking</h3>
                      <div className="space-y-3 text-gray-300">
                        <p><strong>Option 1:</strong> Log in to your customer dashboard and click "Cancel Booking" on your active booking.</p>
                        <p><strong>Option 2:</strong> Contact us directly via WhatsApp or email with your booking reference.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Important Notes */}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-6">
                  <h4 className="text-xl font-bold text-amber-500 mb-3">Important Notes</h4>
                  <ul className="space-y-2 text-gray-300">
                    <li>• The 48-hour period is calculated from your scheduled service time</li>
                    <li>• Refunds are processed to the original payment method</li>
                    <li>• In case of emergencies, please contact us directly - we may consider exceptions</li>
                    <li>• Weather-related cancellations are handled on a case-by-case basis</li>
                    <li>• For flight delays affecting airport transfers, please notify us immediately</li>
                  </ul>
                </div>

                {/* Modifications */}
                <div className="border-t border-gray-800 pt-8">
                  <h3 className="text-2xl font-bold text-white mb-4">Modifying Your Booking</h3>
                  <p className="text-gray-300 mb-4">
                    Need to change your booking details instead of cancelling? Contact us at least 24 hours before your service to modify:
                  </p>
                  <ul className="space-y-2 text-gray-300 ml-4">
                    <li>• Pickup/drop-off locations</li>
                    <li>• Date and time (subject to availability)</li>
                    <li>• Vehicle type (price difference may apply)</li>
                    <li>• Number of passengers</li>
                  </ul>
                </div>

                {/* Contact */}
                <div className="border-t border-gray-800 pt-8 text-center">
                  <h3 className="text-2xl font-bold text-white mb-4">Questions?</h3>
                  <p className="text-gray-300 mb-6">
                    If you have any questions about our cancellation policy, please don't hesitate to contact us.
                  </p>
                  <div className="flex flex-wrap justify-center gap-4">
                    <Button
                      onClick={() => navigate("/booking")}
                      className="bg-gradient-to-r from-amber-600 to-amber-500 text-black font-bold hover:from-amber-500 hover:to-amber-400"
                    >
                      Make a Booking
                    </Button>
                    <Button
                      onClick={() => navigate("/")}
                      variant="outline"
                      className="border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
                    >
                      Back to Home
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-black to-gray-900 text-white py-12 px-4 border-t border-amber-500/20">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">Elite CWB - London's Premier Chauffeur Service</p>
          <p className="text-gray-600 text-sm mt-2">© 2025 Elite CWB. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default CancellationPolicy;
